/**
 * useUpdateDisplayName Hook
 *
 * Custom hook for updating the user's display name in both
 * Firebase Auth and Firestore user document.
 */

import { useState, useCallback } from 'react';
import { updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from './useAuth';

/**
 * Return type for useUpdateDisplayName hook
 */
interface UseUpdateDisplayNameResult {
  /**
   * Update the user's display name
   *
   * @param newName - The new display name to set
   */
  updateDisplayName: (newName: string) => Promise<void>;
  /** Whether an update is in progress */
  loading: boolean;
  /** Error message if update failed (null if no error) */
  error: string | null;
}

/**
 * Hook to update user display name in Firebase Auth and Firestore
 *
 * Updates both the Firebase Auth profile and the Firestore user document
 * to keep them in sync.
 *
 * @example
 * ```tsx
 * const { updateDisplayName, loading, error } = useUpdateDisplayName();
 *
 * const handleSave = async () => {
 *   await updateDisplayName('New Name');
 * };
 * ```
 */
export const useUpdateDisplayName = (): UseUpdateDisplayNameResult => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateDisplayName = useCallback(
    async (newName: string) => {
      if (!user) {
        setError('Not authenticated');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Update Firebase Auth profile
        await updateProfile(user, { displayName: newName });

        // Update Firestore user document
        const db = getFirebaseDb();
        await setDoc(doc(db, 'users', user.uid), { displayName: newName }, { merge: true });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update name';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  return { updateDisplayName, loading, error };
};
